import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronsUpDown, ChevronUp, ChevronDown, Pencil, Eye, Check, X, Trash2 } from 'lucide-react';
import { EntityListLayout, FilterPanel, UserFilters, Pagination } from '../../components';
import type { UserFilterValues } from '../../components/FilterPanel/UserFilters';
import { LoadingOverlay } from '../../components/ExportOverlay';
import { Input } from '../../components/Form/Input';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ActionMenu, type ActionMenuEntry } from '../../components/ui/ActionMenu';
import { useToast } from '../../components/Toast';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
} from '../../components/ui/Table';
import { authService } from '../../services/auth.service';
import { formatRole } from '../../utils/formatters';
import {
  useApproveInvitation,
  useApproveUser,
  useDeleteUser,
  useInviteUser,
  useRejectInvitation,
  useRejectUser,
  useUser,
  useUsers,
} from '../../hooks/useUsers';
import { useSuppliers } from '../../hooks/useSuppliers';
import { USER_ROLES } from '@ministry-of-wine/shared';

interface Supplier {
  id: number;
  name: string;
}

interface PendingInvitation {
  id: number;
  name: string;
  email: string;
  role: string;
  supplierName?: string | null;
  dateCreated: string;
  approvalStatus?: string;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
};

interface InviteFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  supplierId: string;
}

const showEmail = true;
const showRole = true;
const showSupplier = true;
const showStatus = true;
const showDateCreated = true;
const showActions = true;

export const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState<number | 'all'>(10);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<UserFilterValues | null>(null);
  const [currentFilters, setCurrentFilters] = useState<UserFilterValues | null>(null);
  const [inviteFormData, setInviteFormData] = useState<InviteFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    supplierId: '',
  });
  const [inviteErrors, setInviteErrors] = useState<Partial<InviteFormData>>({});
  const [inviteSubmitError, setInviteSubmitError] = useState('');
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState('');
  const [userToDelete, setUserToDelete] = useState<{ id: number; name: string } | null>(null);
  const [userToReject, setUserToReject] = useState<{ id: number; name: string } | null>(null);
  const [invitationToReject, setInvitationToReject] = useState<{ id: number; name: string } | null>(null);
  const toast = useToast();
  const currentUser = authService.getStoredUser();
  const isSupplierAdmin = currentUser?.role === 'supplier_admin';
  const isSuperAdmin = currentUser?.role === USER_ROLES.SUPERADMIN;
  const deleteUserMutation = useDeleteUser();
  const approveUserMutation = useApproveUser();
  const rejectUserMutation = useRejectUser();
  const approveInvitationMutation = useApproveInvitation();
  const rejectInvitationMutation = useRejectInvitation();
  const { data: currentUserDetails } = useUser(
    currentUser?.id ?? '',
    Boolean(currentUser?.id) && isSupplierAdmin,
  );
  const suppliersParams = useMemo(() => new URLSearchParams({ page: '1', limit: '1000' }), []);
  const { data: suppliersResponse } = useSuppliers(suppliersParams);
  const suppliers = (suppliersResponse?.data || []) as Supplier[];
  const inviteUserMutation = useInviteUser();

  const supplierAdminSupplierId =
    currentUser?.supplierId !== null && currentUser?.supplierId !== undefined
      ? String(currentUser.supplierId)
      : currentUserDetails?.supplierId !== null && currentUserDetails?.supplierId !== undefined
        ? String(currentUserDetails.supplierId)
        : '';
  const supplierAdminSupplierName =
    currentUser?.supplierName ||
    currentUserDetails?.supplierName ||
    suppliers.find((supplier) => String(supplier.id) === supplierAdminSupplierId)?.name ||
    '';
  const inviteFromQuery = searchParams.get('invite') === 'true';
  const preselectedSupplierId = searchParams.get('supplierId') || '';
  const getDefaultInviteRole = (supplierId: string) => (supplierId ? 'supplier_admin' : '');

  const queryParams = useMemo(() => {
    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: itemsPerPage === 'all' ? '1000000' : itemsPerPage.toString(),
    });

    if (activeFilters) {
      if (activeFilters.supplierName) params.append('supplierName', activeFilters.supplierName);
      if (activeFilters.userName) params.append('name', activeFilters.userName);
      if (activeFilters.email) params.append('email', activeFilters.email);
      if (activeFilters.phoneNumber) params.append('phone', activeFilters.phoneNumber);
      if (activeFilters.role) params.append('role', activeFilters.role);
      if (activeFilters.status) params.append('status', activeFilters.status);
    }

    return params;
  }, [activeFilters, currentPage, itemsPerPage]);

  const { data: usersResponse, isLoading: loading, isFetching } = useUsers(queryParams);
  const users = usersResponse?.data || [];
  const pendingInvitations = (usersResponse as { pendingInvitations?: PendingInvitation[] })?.pendingInvitations || [];
  const totalEntries = usersResponse?.pagination?.total || 0;
  const totalPages = usersResponse?.pagination?.totalPages || 1;

  useEffect(() => {
    if (!inviteFromQuery) {
      return;
    }

    setInviteSuccessMessage('');
    setInviteSubmitError('');
    setInviteFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      supplierId: isSupplierAdmin ? supplierAdminSupplierId : preselectedSupplierId,
      role: getDefaultInviteRole(isSupplierAdmin ? supplierAdminSupplierId : preselectedSupplierId),
    });
    setInviteErrors({});
    setIsInviteOpen(true);
  }, [inviteFromQuery, isSupplierAdmin, preselectedSupplierId, supplierAdminSupplierId]);

  useEffect(() => {
    if (!isSupplierAdmin || !supplierAdminSupplierId) {
      return;
    }

    setInviteFormData((prev) => {
      const nextRole = prev.role === 'superadmin' || !prev.role ? 'supplier_admin' : prev.role;
      if (prev.supplierId === supplierAdminSupplierId && nextRole === prev.role) {
        return prev;
      }

      return {
        ...prev,
        supplierId: supplierAdminSupplierId,
        role: nextRole,
      };
    });

    setInviteErrors((prev) => ({ ...prev, supplierId: undefined, role: undefined }));
  }, [isSupplierAdmin, supplierAdminSupplierId]);

  const resetInviteForm = () => {
    const supplierId = isSupplierAdmin ? supplierAdminSupplierId : '';
    setInviteFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      supplierId,
      role: getDefaultInviteRole(supplierId),
    });
    setInviteErrors({});
    setInviteSubmitError('');
  };

  const handleInviteChange = (field: keyof InviteFormData, value: string) => {
    if (isSupplierAdmin && field === 'supplierId') {
      return;
    }

    setInviteSuccessMessage('');
    setInviteSubmitError('');
    setInviteFormData((prev) => {
      if (field !== 'supplierId') {
        return { ...prev, [field]: value };
      }

      const nextSupplierId = value;
      const nextRole =
        nextSupplierId && !prev.role ? 'supplier_admin' : !nextSupplierId && prev.role === 'supplier_admin' ? '' : prev.role;

      return {
        ...prev,
        supplierId: nextSupplierId,
        role: nextRole,
      };
    });

    if (inviteErrors[field]) {
      setInviteErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateInviteForm = () => {
    const newErrors: Partial<InviteFormData> = {};

    if (!inviteFormData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!inviteFormData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!inviteFormData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteFormData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!inviteFormData.role.trim()) {
      newErrors.role = 'Role is required';
    } else if (isSupplierAdmin && inviteFormData.role === 'superadmin') {
      newErrors.role = 'Supplier admins cannot invite super admin users';
    }

    if (isSupplierAdmin) {
      if (!supplierAdminSupplierId) {
        newErrors.supplierId = 'Your account must be assigned to a supplier before inviting users';
      } else if (inviteFormData.supplierId !== supplierAdminSupplierId) {
        newErrors.supplierId = 'Supplier admins can only assign users to their own supplier';
      }
    }

    setInviteErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInviteSubmit = async () => {
    if (!validateInviteForm()) {
      return;
    }

    try {
      const response = await inviteUserMutation.mutateAsync({
        firstName: inviteFormData.firstName.trim(),
        lastName: inviteFormData.lastName.trim(),
        email: inviteFormData.email.trim(),
        phone: inviteFormData.phone.trim() || undefined,
        role: inviteFormData.role,
        supplierId:
          isSupplierAdmin
            ? parseInt(supplierAdminSupplierId, 10)
            : inviteFormData.supplierId
              ? parseInt(inviteFormData.supplierId, 10)
              : null,
      });

      setIsInviteOpen(false);
      setTimeout(() => {
        resetInviteForm();
        setInviteSuccessMessage(response.message || 'Invitation sent successfully.');
      }, 300);
    } catch (error: unknown) {
      const message =
        typeof error === 'object' && error !== null && 'message' in error
          ? Array.isArray((error as { message?: string | string[] }).message)
            ? (error as { message?: string[] }).message?.[0]
            : (error as { message?: string }).message
          : 'Failed to send invitation. Please try again.';

      setInviteSubmitError(message || 'Failed to send invitation. Please try again.');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon: React.FC<{ field: string }> = ({ field }) => {
    if (sortField !== field) {
      return (
        <ChevronsUpDown size={16} color="#D0D5DD" className="inline-block ml-1" aria-hidden="true" />
      );
    }

    return sortDirection === 'asc' ? (
      <ChevronUp size={16} color="#404040" className="inline-block ml-1" aria-hidden="true" />
    ) : (
      <ChevronDown size={16} color="#404040" className="inline-block ml-1" aria-hidden="true" />
    );
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    const modifier = sortDirection === 'asc' ? 1 : -1;

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal) * modifier;
    }

    return (aVal > bVal ? 1 : -1) * modifier;
  });

  const handlePageSizeChange = (pageSize: number | 'all') => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) {
      return;
    }

    const { name } = userToDelete;
    try {
      await deleteUserMutation.mutateAsync(userToDelete.id);
      toast.success(`${name} deleted`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user. Please try again.'));
    } finally {
      setUserToDelete(null);
    }
  };

  const handleApproveUser = async (user: { id: number; name: string }) => {
    try {
      await approveUserMutation.mutateAsync(user.id);
      toast.success(`${user.name} approved`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve user. Please try again.'));
    }
  };

  const handleConfirmReject = async () => {
    if (!userToReject) {
      return;
    }

    const { name } = userToReject;
    try {
      await rejectUserMutation.mutateAsync(userToReject.id);
      toast.success(`${name} rejected`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject user. Please try again.'));
    } finally {
      setUserToReject(null);
    }
  };

  const handleApproveInvitation = async (invitation: { id: number; name: string }) => {
    try {
      await approveInvitationMutation.mutateAsync(invitation.id);
      toast.success(`Invitation for ${invitation.name} approved`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve invitation. Please try again.'));
    }
  };

  const handleConfirmRejectInvitation = async () => {
    if (!invitationToReject) {
      return;
    }

    const { name } = invitationToReject;
    try {
      await rejectInvitationMutation.mutateAsync(invitationToReject.id);
      toast.success(`Invitation for ${name} rejected`);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject invitation. Please try again.'));
    } finally {
      setInvitationToReject(null);
    }
  };

  const buildUserActions = (user: {
    id: number;
    name: string;
    status: string;
    deletedAt?: string | null;
  }): ActionMenuEntry[] => {
    const notSelf = Number(user.id) !== Number(currentUser?.id);

    // CRUD group.
    const crud: ActionMenuEntry[] = [
      {
        label: 'View',
        icon: <Eye size={16} />,
        onClick: () => navigate(`/users/${user.id}`),
      },
    ];
    if (!user.deletedAt) {
      crud.push({
        label: 'Edit',
        icon: <Pencil size={16} />,
        onClick: () => navigate(`/users/${user.id}/edit`),
      });
    }
    if (isSuperAdmin && !user.deletedAt && notSelf) {
      crud.push({
        label: 'Delete',
        icon: <Trash2 size={16} />,
        destructive: true,
        onClick: () => setUserToDelete({ id: user.id, name: user.name }),
        disabled: deleteUserMutation.isPending,
      });
    }

    // Approval group — Approve reinstates pending/rejected; Reject revokes pending/active.
    const approval: ActionMenuEntry[] = [];
    if (isSuperAdmin && !user.deletedAt) {
      if (user.status === 'pending_approval' || user.status === 'rejected') {
        approval.push({
          label: 'Approve',
          icon: <Check size={16} />,
          onClick: () => handleApproveUser({ id: user.id, name: user.name }),
          disabled: approveUserMutation.isPending,
        });
      }
      if ((user.status === 'pending_approval' || user.status === 'active') && notSelf) {
        approval.push({
          label: 'Reject',
          icon: <X size={16} />,
          destructive: true,
          onClick: () => setUserToReject({ id: user.id, name: user.name }),
          disabled: rejectUserMutation.isPending,
        });
      }
    }

    return crud.length && approval.length
      ? [...crud, { divider: true }, ...approval]
      : [...crud, ...approval];
  };

  return (
    <EntityListLayout
      title="Users"
      addButtonLabel="Add New User"
      // Only super admins can create users directly; supplier admins use "Invite User".
      onAdd={isSuperAdmin ? () => navigate('/users/new') : undefined}
      headerActions={
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setInviteSuccessMessage('');
            resetInviteForm();
            setIsInviteOpen(true);
          }}
          className="h-[38px] rounded-[6px] px-4 text-[14px]"
        >
          Invite User
        </Button>
      }
      entriesCount={totalEntries}
      onOpenFilter={() => setIsFilterOpen(true)}
      currentPageSize={itemsPerPage}
      onPageSizeChange={handlePageSizeChange}
      isLoading={loading && users.length === 0}
      loadingText="Loading..."
    >
      {inviteSuccessMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {inviteSuccessMessage}
        </div>
      )}

      <div className="relative">
        <LoadingOverlay visible={isFetching && users.length > 0} message="Loading..." />
        <Card noPadding>
        <div className="overflow-x-auto">
        <Table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell onClick={() => handleSort('name')} style={{ cursor: 'pointer', width: '16%' }}>
                Name
                <SortIcon field="name" />
              </TableHeaderCell>
              {showEmail && (
                <TableHeaderCell onClick={() => handleSort('email')} style={{ cursor: 'pointer', width: '18%' }}>
                  Email
                  <SortIcon field="email" />
                </TableHeaderCell>
              )}
              {showRole && (
                <TableHeaderCell onClick={() => handleSort('role')} style={{ cursor: 'pointer', width: '11%' }}>
                  Role
                  <SortIcon field="role" />
                </TableHeaderCell>
              )}
              {showSupplier && (
                <TableHeaderCell
                  onClick={() => handleSort('supplierName')}
                  style={{ cursor: 'pointer', width: '16%' }}
                >
                  Supplier
                  <SortIcon field="supplierName" />
                </TableHeaderCell>
              )}
              {showStatus && (
                <TableHeaderCell
                  onClick={() => handleSort('status')}
                  style={{ cursor: 'pointer', width: '17%' }}
                >
                  Status
                  <SortIcon field="status" />
                </TableHeaderCell>
              )}
              {showDateCreated && (
                <TableHeaderCell
                  onClick={() => handleSort('dateCreated')}
                  style={{ cursor: 'pointer', width: '15%' }}
                >
                  Date Created
                  <SortIcon field="dateCreated" />
                </TableHeaderCell>
              )}
              {showActions && <TableHeaderCell style={{ width: '7%' }}>Actions</TableHeaderCell>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pendingInvitations.map((invitation) => (
              <TableRow key={`invitation-${invitation.id}`}>
                <TableCell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {invitation.name}
                </TableCell>
                {showEmail && (
                  <TableCell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {invitation.email}
                  </TableCell>
                )}
                {showRole && <TableCell>{formatRole(invitation.role)}</TableCell>}
                {showSupplier && (
                  <TableCell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {invitation.supplierName || '-'}
                  </TableCell>
                )}
                {showStatus && (
                  <TableCell>
                    {invitation.approvalStatus === 'rejected' ? (
                      <span
                        className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#FEE2E2', color: '#B42318' }}
                      >
                        Rejected
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                      >
                        Pending approval
                      </span>
                    )}
                  </TableCell>
                )}
                {showDateCreated && (
                  <TableCell>
                    {new Date(invitation.dateCreated).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                )}
                {showActions && (
                  <TableCell>
                    {/* Approve/Reject is super-admin only and applies to pending
                        invitations; supplier admins see invitations read-only. */}
                    {isSuperAdmin && invitation.approvalStatus !== 'rejected' && (
                      <ActionMenu
                        ariaLabel={`Actions for invitation ${invitation.name}`}
                        items={[
                          {
                            label: 'Approve',
                            icon: <Check size={16} />,
                            onClick: () =>
                              handleApproveInvitation({ id: invitation.id, name: invitation.name }),
                            disabled: approveInvitationMutation.isPending,
                          },
                          {
                            label: 'Reject',
                            icon: <X size={16} />,
                            destructive: true,
                            onClick: () =>
                              setInvitationToReject({ id: invitation.id, name: invitation.name }),
                            disabled: rejectInvitationMutation.isPending,
                          },
                        ]}
                      />
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {sortedUsers.map((user) => (
              <TableRow key={user.id} style={user.deletedAt ? { opacity: 0.6 } : undefined}>
                <TableCell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.name}
                </TableCell>
                {showEmail && (
                  <TableCell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.email}
                  </TableCell>
                )}
                {showRole && <TableCell>{formatRole(user.role)}</TableCell>}
                {showSupplier && (
                  <TableCell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.supplierName || '-'}
                  </TableCell>
                )}
                {showStatus && (
                  <TableCell>
                    {user.deletedAt ? (
                      <span
                        className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                      >
                        Deleted
                      </span>
                    ) : user.status === 'rejected' ? (
                      <span
                        className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{ backgroundColor: '#FEE2E2', color: '#B42318' }}
                      >
                        Rejected
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: user.status === 'active' ? '#D1FAE5' : '#FEF3C7',
                          color: user.status === 'active' ? '#065F46' : '#92400E',
                        }}
                      >
                        {user.status === 'active' ? 'Active' : 'Pending approval'}
                      </span>
                    )}
                  </TableCell>
                )}
                {showDateCreated && (
                  <TableCell>
                    {new Date(user.dateCreated).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                )}
                {showActions && (
                  <TableCell>
                    <ActionMenu
                      ariaLabel={`Actions for ${user.name}`}
                      items={buildUserActions(user)}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
        </Card>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalEntries={totalEntries}
        pageSize={typeof itemsPerPage === 'number' ? itemsPerPage : totalEntries}
        onPageChange={setCurrentPage}
        loading={loading}
      />

      <FilterPanel
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onFilter={() => {
          setIsFilterOpen(false);
          setTimeout(() => {
            if (currentFilters) {
              setActiveFilters(currentFilters);
            }
            setCurrentPage(1);
          }, 300);
        }}
        onClear={() => {
          setIsFilterOpen(false);
          setTimeout(() => {
            setActiveFilters(null);
            setCurrentFilters(null);
            setCurrentPage(1);
          }, 300);
        }}
      >
        <UserFilters
          initialValues={activeFilters || undefined}
          onFiltersReady={(getFilters) => {
            setCurrentFilters(getFilters());
          }}
        />
      </FilterPanel>

      <FilterPanel
        isOpen={isInviteOpen}
        onClose={() => {
          setIsInviteOpen(false);
          setInviteSubmitError('');
        }}
        onFilter={handleInviteSubmit}
        onClear={resetInviteForm}
        primaryButtonLabel="Send Invite"
        primaryTestId="invite-submit"
        isPrimaryLoading={inviteUserMutation.isPending}
      >
        <div className="w-full" style={{ paddingTop: '24px' }}>
          <h2
            style={{
              fontFamily: 'Oxygen',
              fontSize: '24px',
              fontWeight: 700,
              color: '#101828',
              marginBottom: '12px',
            }}
          >
            Invite User
          </h2>

          <p className="text-sm text-neutral-600 mb-6 pr-6">
            Send an invitation email so the user can set their password and activate their account.
          </p>

          {inviteSubmitError && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg text-error-700 text-sm">
              {inviteSubmitError}
            </div>
          )}

          <div className="space-y-6 pr-6">
            <Input
              label="First Name"
              name="firstName"
              autoComplete="given-name"
              value={inviteFormData.firstName}
              onChange={(e) => handleInviteChange('firstName', e.target.value)}
              error={inviteErrors.firstName}
              placeholder="Enter first name"
            />

            <Input
              label="Last Name"
              name="lastName"
              autoComplete="family-name"
              value={inviteFormData.lastName}
              onChange={(e) => handleInviteChange('lastName', e.target.value)}
              error={inviteErrors.lastName}
              placeholder="Enter last name"
            />

            <Input
              label="Email"
              type="email"
              name="email"
              autoComplete="email"
              value={inviteFormData.email}
              onChange={(e) => handleInviteChange('email', e.target.value)}
              error={inviteErrors.email}
              placeholder="Enter email address"
            />

            <Input
              label="Phone"
              type="tel"
              name="phone"
              autoComplete="tel"
              value={inviteFormData.phone}
              onChange={(e) => handleInviteChange('phone', e.target.value)}
              placeholder="Enter phone number"
            />

            <div>
              <label
                style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px',
                }}
              >
                Role
              </label>
              <select
                value={inviteFormData.role}
                onChange={(e) => handleInviteChange('role', e.target.value)}
                style={{
                  width: '100%',
                  height: '44px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: inviteErrors.role ? '1px solid #DC2626' : '1px solid #D0D5DD',
                  fontFamily: 'Oxygen',
                  fontSize: '16px',
                  color: '#101828',
                  backgroundColor: 'white',
                }}
              >
                <option value="">Select a role</option>
                {!isSupplierAdmin && <option value="superadmin">Super Admin</option>}
                <option value="supplier_admin">Supplier Admin</option>
                <option value="supplier_user">Supplier User</option>
              </select>
              {inviteErrors.role && (
                <p style={{ color: '#DC2626', fontSize: '14px', marginTop: '6px' }}>
                  {inviteErrors.role}
                </p>
              )}
            </div>

            <div>
              <label
                style={{
                  fontFamily: 'Oxygen',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#344054',
                  display: 'block',
                  marginBottom: '6px',
                }}
              >
                Supplier
              </label>
              {isSupplierAdmin ? (
                <Input
                  value={supplierAdminSupplierName}
                  readOnly
                  placeholder="Loading supplier..."
                  error={inviteErrors.supplierId}
                />
              ) : (
                <>
                  <select
                    value={inviteFormData.supplierId}
                    onChange={(e) => handleInviteChange('supplierId', e.target.value)}
                    style={{
                      width: '100%',
                      height: '44px',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: inviteErrors.supplierId ? '1px solid #DC2626' : '1px solid #D0D5DD',
                      fontFamily: 'Oxygen',
                      fontSize: '16px',
                      color: '#101828',
                      backgroundColor: 'white',
                    }}
                  >
                    <option value="">No supplier</option>
                    {suppliers.map((supplier) => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </select>
                  {inviteErrors.supplierId && (
                    <p style={{ color: '#DC2626', fontSize: '14px', marginTop: '6px' }}>
                      {inviteErrors.supplierId}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </FilterPanel>

      <ConfirmDialog
        isOpen={Boolean(userToDelete)}
        title="Delete user"
        message={
          <>
            Delete user <strong>{userToDelete?.name}</strong>? They will be marked as deleted
            and can only be seen by super admins.
          </>
        }
        confirmLabel="Delete"
        loadingLabel="Deleting..."
        cancelLabel="Cancel"
        isLoading={deleteUserMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setUserToDelete(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(userToReject)}
        title="Reject user"
        message={
          <>
            Reject <strong>{userToReject?.name}</strong>? They will be marked as rejected
            and will not be able to sign in.
          </>
        }
        confirmLabel="Reject"
        loadingLabel="Rejecting..."
        cancelLabel="Cancel"
        isLoading={rejectUserMutation.isPending}
        onConfirm={handleConfirmReject}
        onCancel={() => setUserToReject(null)}
      />

      <ConfirmDialog
        isOpen={Boolean(invitationToReject)}
        title="Reject invitation"
        message={
          <>
            Reject the invitation for <strong>{invitationToReject?.name}</strong>? No invite
            email will be sent.
          </>
        }
        confirmLabel="Reject"
        loadingLabel="Rejecting..."
        cancelLabel="Cancel"
        isLoading={rejectInvitationMutation.isPending}
        onConfirm={handleConfirmRejectInvitation}
        onCancel={() => setInvitationToReject(null)}
      />
    </EntityListLayout>
  );
};

export default UsersPage;
